import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Quote } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

export const TestimonialCard = ({ testimonial, index = 0, dark = false }) => {
  const { t } = useLanguage();
  const image = testimonial.imageUrl || testimonial.image || testimonial.avatar;
  const alt = testimonial.imageAlt || testimonial.name;
  const preserveFullImage = image === '/assets/photos/site-updates/grammar-lesson.webp';
  const quote = testimonial.shortQuote || testimonial.title || testimonial.name;
  const excerpt = testimonial.excerpt || testimonial.summary || '';

  if (dark) {
    return (
      <article className="p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300 h-full flex flex-col">
        <Quote size={32} className="text-[#F5A623]/60 mb-4" />
        <h3 className="font-sora text-xl font-semibold text-white mb-3 leading-tight line-clamp-2">
          {quote}
        </h3>
        <p className="text-white/80 leading-relaxed mb-6 flex-1 line-clamp-2">{excerpt}</p>
        <div className="flex items-center gap-4 mb-5">
          {image ? (
            <div className="avatar-ring shrink-0">
              <img src={image} alt={alt} loading="lazy" className="w-12 h-12 object-cover rounded-full" />
            </div>
          ) : null}
          <div>
            <p className="text-white font-semibold">{testimonial.name}</p>
            {testimonial.title ? <p className="text-white/80 text-sm">{testimonial.title}</p> : null}
            {testimonial.location ? (
              <p className="text-white/80 text-xs flex items-center gap-1">
                <Globe size={12} /> {testimonial.location}
              </p>
            ) : null}
          </div>
        </div>
        <Link
          to={`/testimonials/${testimonial.slug}`}
          className="mt-auto text-[#F5A623] hover:text-white font-medium inline-flex items-center gap-2 transition-colors"
        >
          {t('testimonials.readFullStory')} <ArrowRight size={16} />
        </Link>
      </article>
    );
  }

  const accents = ['border-t-[#D72B2B]', 'border-t-[#4CAF50]', 'border-t-[#F5A623]', 'border-t-[#1A2E6E]'];

  return (
    <article className={`glass-card overflow-hidden border-t-4 ${accents[index % accents.length]} hover:shadow-xl transition-all duration-300 flex flex-col h-full`}>
      {image ? (
        <div className="relative h-52 overflow-hidden">
          <img
            src={image}
            alt={alt}
            loading="lazy"
            className={preserveFullImage ? 'h-full w-full object-contain bg-[#F8F9FC]' : 'photo-fit-card h-full'}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A2E6E]/50 via-transparent to-transparent" />
        </div>
      ) : null}
      <div className="p-8 flex flex-col flex-1">
        <Quote size={32} className="text-[#1A2E6E]/15 mb-4" />
        <h2 className="font-sora text-xl font-semibold text-[#1A2E6E] mb-3 leading-tight">
          {quote}
        </h2>
        <p className="text-[#1A2E6E]/75 leading-relaxed mb-6 flex-1 line-clamp-3">{excerpt}</p>
        <div className="flex items-start justify-between gap-4 mt-auto">
          <div>
            <p className="font-semibold text-[#1A2E6E]">{testimonial.name}</p>
            {testimonial.title ? <p className="text-sm text-[#1A2E6E]/60">{testimonial.title}</p> : null}
            {testimonial.location ? (
              <p className="text-xs text-[#4CAF50] flex items-center gap-1 mt-1">
                <Globe size={12} /> {testimonial.location}
              </p>
            ) : null}
          </div>
          <Link
            to={`/testimonials/${testimonial.slug}`}
            className="text-[#D72B2B] font-semibold inline-flex items-center gap-2 hover:text-[#B71C1C] transition-colors whitespace-nowrap text-sm"
          >
            {t('testimonials.readFullStory')} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default TestimonialCard;
